import React from 'react'
import { FormTypes, SubmissionTypes } from '@oneblink/types'
import {
  submissionService,
  OneBlinkAppsError,
  SetFormSubmission,
} from '@oneblink/apps-react'
import { ExecutedLookups } from '@oneblink/apps-react'
import { toast } from 'react-toastify'
import _cloneDeep from 'lodash.clonedeep'
import jsum from 'jsum'

import LoadingSpinner from '../../../components/LoadingSpinner'
import Dialog from '../../../components/Dialog'

import useSubmitForm from '../../../hooks/useSubmitForm'
import useCancelForm from '../../../hooks/useCancelForm'
import useSaveDraft from '../../../hooks/useSaveDraft'
import useCheckMaintenanceOnSubmit from '../../../hooks/useCheckMaintenanceOnSubmit'
import MaintenanceModal from '../../../components/MaintenanceModel'
import useAutoSaveApprovalsControlledForm from '../../../hooks/useAutoSaveApprovalsControlledForm'
import AutoSaveApprovalsControlledForm from '../../../components/AutoSaveApprovalsControlledForm'

import makeIsShowing from '../../../lib/is-element-showing'
import { generateLicenceChargesTable } from '../../../lib/controlled-forms/food-licensing/licence-charges'
import {
  updateSpinnerState,
  setSpinnerInitial,
  setSpinnerError,
} from '../../../lib/controlled-forms/food-licensing/loading-spinner'
import {
  setHasPricingLookupRunInitial,
  updateHasPricingLookupRun,
  verifyPricingLookupHasRun,
} from '../../../lib/controlled-forms/common/has-pricing-lookup-run'
import getPricingAndCrn from '../../../lib/controlled-forms/common/get-pricing-and-crn'
import {
  generateFacilityLineItems,
  generateApplicationFeeLineItem,
  generateProductLineItems,
} from '../../../lib/controlled-forms/common/generate-invoice-line-items'

import { HTTPError } from '../../../lib/http'

import getPricing from '../../../lib/controlled-forms/common/get-pricing'
import serverValidation from '../../../lib/server-validation'
import { useAppConfig } from '../../../providers/AppConfigProvider'
import { useCustomFunctions } from './Generic'
import {
  findElement,
  replaceElement,
} from '../../../lib/definition-manipulation'
import isDeepEqual from '../../../lib/is-deep-equal'
interface FormProps {
  form: FormTypes.Form
  preFillData: Record<string, unknown>
}

function generatePartialModel(
  submission: Record<string, unknown>,
): Record<string, unknown> {
  return {
    confirmCompanyDetails: submission.confirmCompanyDetails,
    fixedPremises: submission.fixedPremises,
    vehicle: submission.vehicle,
    foodBusiness: submission.foodBusiness,
    _Facility: submission._Facility,
    variationType: submission.Variation_Type,
    action: submission.Action,
    vehicleAdditionActivities: submission.Vehicle_Addition_Activities,
    vehicleLocation: submission.Vehicles_Location,
    vehicleReplacement: submission.Vehicles_Replacement,
    vehicleCancellation: submission.Vehicles_Cancellation,
    vehiclesRegistrationChange: submission.Vehicles_Registration_Change,
    vesselAddition: submission.Vessel_Additions,
    vesselReplacements: submission.Vessel_Replacements,
    vesselCancel: submission.Cancel_Vessel,
    licenseNumber: submission.License_Number,
  }
}

function generateHash(submission: Record<string, unknown>): string {
  return jsum.digest(
    //need to clone deep becoz jsum corrupts the model somehow
    generatePartialModel(_cloneDeep(submission)),
    'sha256',
    'hex',
  )
}

const pricingPaths = {
  permissionsPath: '/pricing/vehicle-vessel-variation/permissions',
  productsPath: '/pricing/food-license/products',
  facilityPath: '/pricing/food-license/facility',
}

export default function VehicleVesselVariation({
  form,
  preFillData,
}: FormProps) {
  const customForm = React.useMemo(() => {
    setHasPricingLookupRunInitial(form, {})
    setSpinnerInitial(form)

    return form
  }, [form])

  const [partialModelHash, setPartialModelHash] = React.useState<string>()

  const [lookupError, setLookupError] = React.useState<string>()
  const [isLookupRunning, setIsLookupRunning] = React.useState(false)

  /*
    `customSetFormSubmission` is handed to every element in the form as its
    `onChange`. Reading these two values from state would put them in its
    dependency list, so the callback would get a new identity whenever either
    changed. Derived-value elements (calculation, summary) re-run their write
    effect on a new `onChange`, and a repeatable set rebuilds the whole entry
    for any write, so a changing identity here is enough to loop the form
    forever. Mirror them into refs and keep the callback stable.
  */
  const partialModelHashRef = React.useRef(partialModelHash)
  const isLookupRunningRef = React.useRef(isLookupRunning)

  const updatePartialModelHash = React.useCallback((hash: string) => {
    partialModelHashRef.current = hash
    setPartialModelHash(hash)
  }, [])

  const updateIsLookupRunning = React.useCallback((running: boolean) => {
    isLookupRunningRef.current = running
    setIsLookupRunning(running)
  }, [])

  const { buttons } = useAppConfig()

  const [onSaveDraft, isSavingDraft] = useSaveDraft({
    formDefinition: form,
    preFillData,
  })
  const [onCancel, cancelError] = useCancelForm(customForm)

  const [isGettingCharges, setIsGettingCharges] = React.useState<boolean>(false)
  const [{ isValidating, serverValidationMessage }, setValidationState] =
    React.useState<{ isValidating: boolean; serverValidationMessage?: string }>(
      { isValidating: false },
    )
  const [{ onSubmit }, { isSubmitting }] = useSubmitForm({
    formDefinition: customForm,
    preFillData,
    skipServerValidation: true,
  })

  const {
    definition,
    liveSubmission,
    setFormSubmission,
    handleSubmit,
    handleCancel,
    handleSaveDraft,
    isAutoSaveSubmissionAvailable,
    isLoadingAutoSaveSubmission,
    startNewSubmission,
    continueAutoSaveSubmission,
    executedLookups,
  } = useAutoSaveApprovalsControlledForm({
    form,
    initialSubmission: preFillData,
    onSubmit,
    onCancel,
    onSaveDraft,
  })

  const { updateDefinition, updateSubmission } = useCustomFunctions(form)

  /* update definition here */
  const customDefinition = React.useMemo(() => {
    let def = definition
    def = generateLicenceChargesTable(liveSubmission, def)
    def = updateSpinnerState(liveSubmission, def)

    if (lookupError) {
      def = setSpinnerError(def, lookupError)
    }

    // update "Licence_Holder_Name" to read only if set by lookup
    if (executedLookups && executedLookups.License_Number) {
      const licenceHolderNameElement = findElement<FormTypes.TextElement>(
        def,
        'Licence_Holder_Name',
      )
      // make license holder name read only if lookup ran and element is populated
      if (liveSubmission.Licence_Holder_Name) {
        if (
          licenceHolderNameElement &&
          !licenceHolderNameElement.value.readOnly
        ) {
          const readOnlyLicenceHolderNameElement = {
            ...licenceHolderNameElement.value,
            readOnly: true,
          }
          licenceHolderNameElement.parent.elements = replaceElement(
            licenceHolderNameElement.parent.elements || [],
            licenceHolderNameElement.value,
            readOnlyLicenceHolderNameElement,
          )
        }
        // make license holder name editable if lookup ran and element is not populated
      } else {
        if (
          licenceHolderNameElement &&
          licenceHolderNameElement.value.readOnly
        ) {
          const readOnlyLicenceHolderNameElement = {
            ...licenceHolderNameElement.value,
            readOnly: false,
          }
          licenceHolderNameElement.parent.elements = replaceElement(
            licenceHolderNameElement.parent.elements || [],
            licenceHolderNameElement.value,
            readOnlyLicenceHolderNameElement,
          )
        }
      }
    }

    return { ...updateDefinition(liveSubmission, def) }
  }, [
    definition,
    liveSubmission,
    lookupError,
    updateDefinition,
    executedLookups,
  ])

  /* update submission data here */
  const customSetFormSubmission: SetFormSubmission = React.useCallback(
    (formSubmission) => {
      setFormSubmission((currentState) => {
        const newFormSubmission: {
          definition: FormTypes.Form
          submission: SubmissionTypes.S3SubmissionData['submission']
          lastElementUpdated: FormTypes.FormElement | undefined
          executedLookups: ExecutedLookups
        } =
          typeof formSubmission === 'function'
            ? formSubmission(currentState)
            : formSubmission

        const { submission, definition } = newFormSubmission
        if (submission.fauxPricingLookup__exclude === 'calculateCharges') {
          if (!isLookupRunningRef.current) {
            updateIsLookupRunning(true)
            setLookupError(undefined)
            getPricing({ submission, definition, ...pricingPaths })
              .then((result) => {
                if (result) {
                  setFormSubmission((prev) => {
                    return {
                      ...prev,
                      submission: {
                        ...prev.submission,
                        ...result,
                        fauxPricingLookup__exclude: undefined,
                      },
                    }
                  })
                }
              })
              .catch((e) => {
                if (e instanceof HTTPError) {
                  e.status === 666
                    ? setLookupError('Failed to calculate pricing')
                    : setLookupError(e.message)
                }
                setFormSubmission((prev) => {
                  return {
                    ...prev,
                    submission: {
                      ...prev.submission,
                      fauxPricingLookup__exclude: undefined,
                      products: undefined,
                      paymentTotal__hidden: undefined,
                      facilities__hidden: undefined,
                      applicationFee__hidden: undefined,
                      applicationFeeTaxRate__hidden: undefined,
                      applicationFeePriorPaymentRequired__hidden: undefined,
                      hasPricingLookupRun__hidden: undefined,
                    },
                  }
                })
                console.error(e)
              })
              .finally(() => {
                updateIsLookupRunning(false)
              })
          }
        }

        const isShowing = makeIsShowing(submission, definition.elements)

        const additionActivities = submission.Vehicle_Addition_Activities as
          | Array<string>
          | undefined

        const rawMilkOption = 'Dairy transport (raw milk)'
        const gameMeatOption = 'Game meat field harvesting'
        const animalFoodOption = 'Animal food field harvesting'

        if (
          isShowing('Vehicle_Addition_Activities') &&
          Array.isArray(additionActivities)
        ) {
          const lastSelection =
            additionActivities[additionActivities.length - 1]
          // if user clicks raw milk remove game meat and animal food
          if (
            lastSelection === rawMilkOption &&
            (additionActivities.includes(gameMeatOption) ||
              additionActivities.includes(animalFoodOption))
          ) {
            submission.Vehicle_Addition_Activities = additionActivities.filter(
              (species: string) =>
                species !== gameMeatOption && species !== animalFoodOption,
            )
          }

          // if user clicks game meat and animal food remove raw milk
          if (
            (lastSelection === gameMeatOption ||
              lastSelection === animalFoodOption) &&
            additionActivities.includes(rawMilkOption)
          ) {
            submission.Vehicle_Addition_Activities = additionActivities.filter(
              (species: string) => species !== rawMilkOption,
            )
          }
        }

        const vehiclesReplacement = submission.Vehicles_Replacement
        if (
          isShowing('Vehicles_Replacement') &&
          Array.isArray(vehiclesReplacement)
        ) {
          for (const vehicle of vehiclesReplacement) {
            const activities = vehicle.Vehicle_Replacement_Activities
            if (Array.isArray(activities)) {
              const lastSelection = activities[activities.length - 1]
              // if user clicks raw milk remove game meat and animal food
              if (
                lastSelection === rawMilkOption &&
                (activities.includes(gameMeatOption) ||
                  activities.includes(animalFoodOption))
              ) {
                vehicle.Vehicle_Replacement_Activities = activities.filter(
                  (species: string) =>
                    species !== gameMeatOption && species !== animalFoodOption,
                )
              }

              // if user clicks game meat and animal food remove raw milk
              if (
                (lastSelection === gameMeatOption ||
                  lastSelection === animalFoodOption) &&
                activities.includes(rawMilkOption)
              ) {
                vehicle.Vehicle_Replacement_Activities = activities.filter(
                  (species: string) => species !== rawMilkOption,
                )
              }
            }
          }
        }
        //always do this last
        updateHasPricingLookupRun({
          submission: submission,
          currentHash: partialModelHashRef.current,
          newHash: generateHash(submission),
          updateHash: updatePartialModelHash,
        })

        const updatedSubmission = updateSubmission(newFormSubmission.submission)

        /*
          Calculation elements re-run and write their result back whenever the
          `onChange` handed to them changes identity, even when the value is
          identical. Returning a new state object for one of those writes gives
          the renderer a new definition and a new `onChange` for every element,
          which runs the calculations again, and the form never settles. Keeping
          the current state when nothing actually changed breaks that cycle.

          `lastElementUpdated` is deliberately not compared: the competing
          calculations report different elements on every pass, so including it
          would mean never finding the states equal. A write that leaves the
          submission untouched has nothing for a lookup to react to anyway.
        */
        if (
          newFormSubmission.definition === currentState.definition &&
          isDeepEqual(updatedSubmission, currentState.submission) &&
          isDeepEqual(
            newFormSubmission.executedLookups,
            currentState.executedLookups,
          )
        ) {
          return currentState
        }

        return {
          ...currentState,
          ...newFormSubmission,
          submission: updatedSubmission,
        }
      })
    },
    [
      setFormSubmission,
      updatePartialModelHash,
      updateIsLookupRunning,
      updateSubmission,
    ],
  )

  const customHandleSubmission = React.useCallback(
    async (newFormSubmission: submissionService.NewFormSubmission) => {
      //taking all the precautions here and overengineering it
      const clonedNewFormSubmission = _cloneDeep(newFormSubmission)
      let pricingCompleted = false
      setValidationState({ isValidating: true })
      let serverValidationMessage: string | undefined = undefined
      try {
        console.log('initial server validation')
        await serverValidation(
          clonedNewFormSubmission.definition.serverValidation,
          clonedNewFormSubmission.submission,
          clonedNewFormSubmission.definition,
        )
      } catch (e) {
        console.log(e)
        serverValidationMessage =
          e instanceof OneBlinkAppsError
            ? e.message
            : 'Unknown error during validation'
      }

      setValidationState({ isValidating: false, serverValidationMessage })

      if (serverValidationMessage) {
        return
      }

      if (!verifyPricingLookupHasRun({ newFormSubmission })) {
        return
      }

      setIsGettingCharges(true)
      try {
        const pricingFunc = () =>
          getPricing({ ...clonedNewFormSubmission, ...pricingPaths })

        const { pricingResponse, customerReferenceNumber } =
          await getPricingAndCrn(pricingFunc, 'NSW Food Authority')

        const facilityLineItems = generateFacilityLineItems(pricingResponse)
        const applicationFeeLineItem =
          generateApplicationFeeLineItem(pricingResponse)

        const productLineItems = generateProductLineItems(pricingResponse)

        clonedNewFormSubmission.submission = {
          ...clonedNewFormSubmission.submission,
          paymentTotal__hidden: pricingResponse.paymentTotal__hidden,
          customerReferenceNumber__hidden: customerReferenceNumber,
          lineItems: [
            ...facilityLineItems,
            applicationFeeLineItem,
            ...productLineItems,
          ],
        }
        pricingCompleted = true
      } catch (e) {
        console.error(e)
        toast.error('There was an error verifying payment amount', {
          position: 'bottom-right',
          hideProgressBar: true,
        })
      }

      setIsGettingCharges(false)

      //a Hail Mary to prevent premature submissions
      if (!pricingCompleted) {
        return
      }
      handleSubmit(clonedNewFormSubmission)
    },
    [handleSubmit],
  )

  const [
    { isFetchingMaintenanceWindow, fetchMaintenanceWindowError },
    { wrappedSubmitFn, clearMaintenanceMessage },
  ] = useCheckMaintenanceOnSubmit(customHandleSubmission)

  return (
    <>
      <AutoSaveApprovalsControlledForm
        definition={customDefinition}
        submission={liveSubmission}
        onCancel={handleCancel}
        onSubmit={wrappedSubmitFn}
        onSaveDraft={handleSaveDraft}
        setFormSubmission={customSetFormSubmission}
        buttons={buttons}
        isSubmitting={
          isSubmitting || isGettingCharges || isFetchingMaintenanceWindow
        }
        cancelError={cancelError}
        isAutoSaveSubmissionAvailable={isAutoSaveSubmissionAvailable}
        isLoadingAutoSaveSubmission={isLoadingAutoSaveSubmission}
        isSavingDraft={isSavingDraft}
        startNewSubmission={startNewSubmission}
        continueAutoSaveSubmission={continueAutoSaveSubmission}
        executedLookups={executedLookups}
        serverValidationMessage={serverValidationMessage}
        clearServerValidationMessage={() =>
          setValidationState({ isValidating: false })
        }
      />
      <Dialog isOpen={isValidating}>
        <LoadingSpinner>Validating</LoadingSpinner>
      </Dialog>
      <MaintenanceModal
        fetchMaintenanceWindowError={fetchMaintenanceWindowError}
        clearMaintenanceMessage={clearMaintenanceMessage}
      />
    </>
  )
}
